import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { supabase } from "../supabaseClient";
import { useAuth } from "./AuthContext";
import { useFeedback } from "./FeedbackContext";
import { useStorage } from "./StorageContext";
import { buildSmartNotifications } from "../ai/smart/smartNotifications";
import { useUnreadCount } from "./UnreadCountContext";

/**
 * @typedef {Object} Notification
 * @property {string} id
 * @property {string=} userId
 * @property {"studio"|"visitor"|"both"|"any"=} world
 * @property {"offer"|"message"|"booking"|"task"|"system"=} type
 * @property {string} title
 * @property {string} body
 * @property {string} createdAt
 * @property {string|null} readAt
 * @property {boolean=} read
 * @property {{ entity?: string, entityId?: string, route?: string }=} target
 * @property {string=} entityType
 * @property {string=} entityId
 * @property {string=} route
 * @property {"info"|"success"|"warning"|"error"=} severity
 * @property {Record<string, any>=} meta
 */

// simple helpers
const now = () => new Date();

const DEFAULT_PREFS = {
  enabled: true,
  sound: true,
  desktopPush: false, // browser push via Notification API
  leadTimes: {
    dryingEnd: 2,
    bisqueStart: 1,
    glazeStart: 1,
    dispatch: 4,
  },
  quietHours: { start: 22, end: 7 }, // 22:00–07:00
};

const FILE_PREFS = "notifications/prefs.json";
const LOCAL_STORAGE_KEY = "mudo:notifications:v1";
const LEGACY_STORAGE_KEY = "mudo:notifications";
const MAX_NOTIFICATIONS = 400;

const mergePrefs = (disk) => {
  if (disk && typeof disk === "object") {
    const hasKeys = Object.keys(disk || {}).length > 0;
    return hasKeys ? { ...DEFAULT_PREFS, ...disk } : DEFAULT_PREFS;
  }
  return DEFAULT_PREFS;
};

const NotificationsContext = createContext(null);
export const useNotifications = () => useContext(NotificationsContext);

function isQuiet(quiet, date = new Date()) {
  const h = date.getHours();
  if (quiet.start < quiet.end) return h >= quiet.start && h < quiet.end; // same-day window
  return h >= quiet.start || h < quiet.end; // wraps past midnight
}

function safeParse(json, fallback) {
  try {
    const parsed = JSON.parse(json);
    return Array.isArray(parsed) ? parsed : fallback;
  } catch {
    return fallback;
  }
}

function readStoredNotifications() {
  if (typeof window === "undefined") return [];
  const raw = window.localStorage?.getItem(LOCAL_STORAGE_KEY);
  if (raw) return safeParse(raw, []);
  const legacy = window.localStorage?.getItem(LEGACY_STORAGE_KEY);
  if (legacy) return safeParse(legacy, []);
  return [];
}

function parseTimestamp(value) {
  if (value == null || value === "") return null;
  if (typeof value === "number") return value;
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function normalizeNotification(raw = {}) {
  const createdAt =
    parseTimestamp(raw.createdAt) ??
    parseTimestamp(raw.created_at) ??
    Date.now();
  const readAt =
    parseTimestamp(raw.readAt) ??
    parseTimestamp(raw.read_at) ??
    null;
  const isRead =
    raw.read === true ||
    raw.isRead === true ||
    raw.is_read === true ||
    readAt != null;

  const entityType =
    raw.entityType ||
    raw.entity_type ||
    raw.entity?.type ||
    raw.target?.entity ||
    null;
  const entityId =
    raw.entityId ||
    raw.entity_id ||
    raw.entity?.id ||
    raw.target?.entityId ||
    null;
  const route = raw.route || raw.target?.route || null;

  const level = raw.level || raw.severity || raw.kind || "info";
  const title = raw.title || "Notification";
  const message = raw.message || raw.body || raw.text || "";
  const source =
    raw.source ||
    (raw.type?.includes("shop") ? "shop" : null) ||
    (raw.type?.includes("booking") ? "bookings" : null) ||
    (raw.type?.includes("kiln") ? "kiln" : null) ||
    (raw.type?.includes("task") ? "planner" : null) ||
    "system";

  return {
    ...raw,
    id: raw.id,
    userId: raw.userId || raw.user_id,
    world: raw.world || "any",
    createdAt,
    readAt,
    read: isRead,
    isRead,
    level,
    title,
    message,
    source,
    entity: raw.entity || (entityType || entityId ? { type: entityType, id: entityId } : null),
    action: raw.action || (route ? { kind: "NAVIGATE", to: route } : null),
    meta: raw.meta || null,
    entityType,
    entityId,
    route,
    severity: raw.severity || level,
    body: raw.body || message,
  };
}

function matchesWorld(notificationWorld, worldFilter) {
  if (!worldFilter) return true;
  const scope = notificationWorld || "any";
  return scope === "any" || scope === "both" || scope === worldFilter;
}

function isUnread(notification) {
  return !notification?.read && notification?.readAt == null;
}

export function NotificationsProvider({ children }) {
  const { isAuthReady, user } = useAuth() || {};
  const { play, buzz } = useFeedback();
  const storage = useStorage ? useStorage() : null;
  const {
    hasFolder,
    isDriveLinked,
    isDriveAuthed,
    loadJSON,
    saveJSON,
  } = storage || {};

  const hasWorkspace = !!hasFolder || (!!isDriveLinked && !!isDriveAuthed);

  const [prefs, setPrefs] = useState(DEFAULT_PREFS);
  const [userId, setUserId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [remoteNotifications, setRemoteNotifications] = useState([]);
  const [localNotifications, setLocalNotifications] = useState(() => readStoredNotifications());
  const [offerNotifications, setOfferNotifications] = useState([]);
  const bootedRef = useRef(false);
  const debounceRef = useRef(0);
  const remoteIdsRef = useRef(new Set());

  // hydrate local notifications on mount (client only)
  useEffect(() => {
    setLocalNotifications(readStoredNotifications());
  }, []);

  // persist local notifications to localStorage
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(localNotifications));
    } catch {}
  }, [localNotifications]);

  // cross-tab sync (local notifications only)
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const onStorage = (event) => {
      if (event.key === LOCAL_STORAGE_KEY && event.newValue) {
        setLocalNotifications(safeParse(event.newValue, []));
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  // hydrate prefs from current workspace
  useEffect(() => {
    let alive = true;
    bootedRef.current = false;
    (async () => {
      if (!hasWorkspace) {
        if (alive) setPrefs(DEFAULT_PREFS);
        bootedRef.current = true;
        return;
      }
      try {
        const disk = await loadJSON(FILE_PREFS, null);
        if (alive) setPrefs(mergePrefs(disk));
      } finally {
        bootedRef.current = true;
      }
    })();
    return () => { alive = false; };
  }, [hasWorkspace, loadJSON]);

  // persist prefs (debounced) whenever they change
  useEffect(() => {
    if (!bootedRef.current || !hasWorkspace) return;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try { await saveJSON(FILE_PREFS, prefs); } catch {}
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [prefs, hasWorkspace, saveJSON]);

  // rehydrate when other parts restore from Files/Vault
  useEffect(() => {
    const rehydrate = async () => {
      try {
        const disk = await loadJSON(FILE_PREFS, null);
        if (disk && typeof disk === "object") {
          setPrefs(mergePrefs(disk));
        }
      } catch {}
    };
    window.addEventListener("mudo:files-restored", rehydrate);
    window.addEventListener("mudo:vault-restored", rehydrate);
    return () => {
      window.removeEventListener("mudo:files-restored", rehydrate);
      window.removeEventListener("mudo:vault-restored", rehydrate);
    };
  }, [loadJSON]);

  // sync current user (consume AuthContext only; no auth network calls here)
  useEffect(() => {
    if (!isAuthReady) {
      setUserId(null);
      setLoading(false);
      return;
    }
    setUserId(user?.id || null);
    setLoading(false);
  }, [isAuthReady, user?.id]);

  const refresh = useCallback(async () => {
    // Guard: Don't call Supabase if auth isn't ready
    if (!isAuthReady) {
      setLoading(false);
      return;
    }
    if (!supabase || !userId) {
      setRemoteNotifications([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("notifications")
        .select("*")
        .eq("user_id", userId)
        .order("created_at", { ascending: false })
        .limit(200);
      if (error) {
        console.error("[NotificationsContext] load error", error);
      }
      const normalized = (data || []).map(normalizeNotification);
      setRemoteNotifications(normalized);
    } catch (err) {
      console.error("[NotificationsContext] load exception", err);
      setRemoteNotifications([]);
    } finally {
      setLoading(false);
    }
  }, [isAuthReady, userId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // keep a set of remote IDs for quick lookups
  useEffect(() => {
    remoteIdsRef.current = new Set((remoteNotifications || []).map((n) => n.id));
  }, [remoteNotifications]);

  // realtime updates
  useEffect(() => {
    if (!isAuthReady) return undefined;
    if (!userId || !supabase?.channel) return undefined;
    const channel = supabase
      .channel(`notifications_${userId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "notifications",
          filter: `user_id=eq.${userId}`,
        },
        (payload) => {
          const { eventType } = payload || {};
          if (eventType === "INSERT") {
            const next = normalizeNotification(payload.new || {});
            setRemoteNotifications((prev) => [next, ...prev.filter((n) => n.id !== next.id)]);
          } else if (eventType === "UPDATE") {
            const next = normalizeNotification(payload.new || {});
            setRemoteNotifications((prev) =>
              prev.map((n) => (n.id === next.id ? { ...n, ...next } : n))
            );
          } else if (eventType === "DELETE") {
            const id = payload.old?.id;
            if (id) {
              setRemoteNotifications((prev) => prev.filter((n) => n.id !== id));
            }
          }
        }
      )
      .subscribe();
    return () => {
      try {
        supabase.removeChannel(channel);
      } catch {}
    };
  }, [isAuthReady, userId]);

  // keep scheduled timers so we can clear/resync when events change
  const timersRef = useRef(new Map());

  // Desktop Notification permission
  async function ensurePushPermission() {
    if (!prefs.desktopPush) return false;
    if (typeof Notification === "undefined") return false;
    if (Notification.permission === "granted") return true;
    if (Notification.permission === "denied") return false;
    const res = await Notification.requestPermission();
    return res === "granted";
  }

  function pushDesktop(title, body) {
    if (!prefs.desktopPush || typeof Notification === "undefined") return;
    if (Notification.permission !== "granted") return;
    try {
      new Notification(title, { body, icon: "/icons/icon-192.png" });
    } catch {}
  }

  const addNotification = useCallback((partial = {}) => {
    const normalizedWorld = partial.world || "any";
    const id =
      partial.id ||
      (globalThis.crypto?.randomUUID
        ? crypto.randomUUID()
        : `notif_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`);
    const record = normalizeNotification({
      ...partial,
      id,
      world: normalizedWorld,
      createdAt: partial.createdAt ?? Date.now(),
      readAt: partial.readAt ?? null,
    });
    setLocalNotifications((prev) => {
      // Dedupe packaging notifications by dedupeKey
      const dedupeKey = partial.dedupeKey || null;
      if (dedupeKey) {
        const duplicate = prev.find((n) => 
          n.dedupeKey === dedupeKey && 
          !n.readAt && 
          !n.isRead && 
          !n.read
        );
        if (duplicate) {
          // Update existing unread notification instead of adding duplicate
          return prev.map((n) => 
            n.id === duplicate.id 
              ? { ...n, createdAt: record.createdAt, lastSeenAt: Date.now() }
              : n
          );
        }
      }
      
      const existing = prev.find((n) => n.id === id);
      const readAt = existing?.readAt ?? record.readAt ?? null;
      const merged = { ...(existing || {}), ...record, readAt, dedupeKey };
      const deduped = prev.filter((n) => n.id !== id);
      return [merged, ...deduped].slice(0, MAX_NOTIFICATIONS);
    });
    return id;
  }, []);

  const addSmartNotification = useCallback(
    (partial = {}) => {
      const id = partial.id || `smart-${Date.now()}`;
      addNotification({
        id,
        createdAt: Date.now(),
        ...partial,
      });
    },
    [addNotification]
  );

  const refreshSmartNotifications = useCallback(
    (context = {}) => {
      const smart = buildSmartNotifications(context);
      smart.forEach((n) => addSmartNotification(n));
    },
    [addSmartNotification]
  );

  const markRead = useCallback(
    async (id) => {
      if (!id) return;
      const readAt = Date.now();
      setLocalNotifications((prev) =>
        prev.map((n) => (n.id === id && isUnread(n) ? { ...n, readAt, read: true, isRead: true } : n))
      );
      setRemoteNotifications((prev) =>
        prev.map((n) => (n.id === id && isUnread(n) ? { ...n, readAt, read: true, isRead: true } : n))
      );
      if (userId && supabase && remoteIdsRef.current.has(id)) {
        try {
          const readAtIso = new Date(readAt).toISOString();
          await supabase
            .from("notifications")
            .update({ read_at: readAtIso })
            .eq("id", id)
            .eq("user_id", userId);
        } catch (err) {
          console.warn("[NotificationsContext] markRead failed", err);
        }
      }
    },
    [userId]
  );

  const markAllRead = useCallback(
    async (worldFilter) => {
      const readAt = Date.now();
      setLocalNotifications((prev) =>
        prev.map((n) => {
          if (!isUnread(n)) return n;
          if (!matchesWorld(n.world, worldFilter)) return n;
          return { ...n, readAt, read: true, isRead: true };
        })
      );
      setRemoteNotifications((prev) =>
        prev.map((n) => {
          if (!isUnread(n)) return n;
          if (!matchesWorld(n.world, worldFilter)) return n;
          return { ...n, readAt, read: true, isRead: true };
        })
      );
      if (userId && supabase) {
        try {
          const readAtIso = new Date(readAt).toISOString();
          let query = supabase
            .from("notifications")
            .update({ read_at: readAtIso })
            .eq("user_id", userId)
            .is("read_at", null);
          if (worldFilter) {
            query = query.in("world", [worldFilter, "both", "any"]);
          }
          await query;
        } catch (err) {
          console.warn("[NotificationsContext] markAllRead failed", err);
        }
      }
    },
    [userId]
  );

  const removeNotification = useCallback(
    async (id) => {
      if (!id) return;
      setLocalNotifications((prev) => prev.filter((n) => n.id !== id));
      setRemoteNotifications((prev) => prev.filter((n) => n.id !== id));
      if (userId && supabase && remoteIdsRef.current.has(id)) {
        try {
          await supabase
            .from("notifications")
            .delete()
            .eq("id", id)
            .eq("user_id", userId);
        } catch (err) {
          console.warn("[NotificationsContext] removeNotification failed", err);
        }
      }
    },
    [userId]
  );

  const clearAllNotifications = useCallback(async () => {
    setLocalNotifications([]);
    setRemoteNotifications([]);
    try {
      if (typeof window !== "undefined") {
        window.localStorage?.setItem(LOCAL_STORAGE_KEY, "[]");
      }
    } catch {}
    if (userId && supabase) {
      try {
        await supabase.from("notifications").delete().eq("user_id", userId);
      } catch (err) {
        console.warn("[NotificationsContext] clearAll failed", err);
      }
    }
  }, [userId]);

  function dispatchToast(payload) {
    try {
      document.dispatchEvent(new CustomEvent("mudo:notify", { detail: payload }));
    } catch {}
  }

  function notify(payload, bodyArg, kindArg) {
    const input =
      typeof payload === "string"
        ? { title: payload, body: bodyArg, kind: kindArg }
        : payload || {};
    const title = input.title || "Notification";
    const body = input.body || input.message || "";
    const kind = input.kind || input.level || input.severity || "info";
    const sound = input.sound ?? prefs.sound;
    if (!prefs.enabled) return;

    // quiet hours – still dispatch toast event, but suppress sound/push
    const quiet = isQuiet(prefs.quietHours);

    dispatchToast({
      title,
      message: body,
      kind: quiet ? "silent" : kind,
      timestamp: Date.now(),
    });

    if (!quiet && sound) {
      const sfx =
        kind === "success" ? "success" :
        kind === "error" ? "error" : "tap";
      play(sfx);
      if (kind === "success") buzz("success");
      else if (kind === "error") buzz("error");
      else buzz("light");
    }

    if (!quiet) pushDesktop(title, body);
  }

  const notifyAndInbox = useCallback(
    (payload = {}) => {
      const { notification, ...rest } = payload;
      const title = notification?.title || rest.title;
      const message = notification?.message || rest.body || rest.message;
      const level = notification?.level || rest.kind || rest.level || rest.severity || "info";
      if (title || message) {
        notify({ title, body: message, kind: level });
      }
      addNotification({
        ...notification,
        title: title || notification?.title || "Notification",
        message: message || "",
        level,
        source: notification?.source || rest.source,
        world: notification?.world || rest.world,
        entity: notification?.entity || rest.entity,
        action: notification?.action || rest.action,
        meta: notification?.meta || rest.meta,
        type: notification?.type || rest.type,
      });
    },
    [addNotification, notify]
  );

  const api = useMemo(
    () => ({
      prefs,
      setPrefs,
      notify,
      notifyAndInbox,
      ensurePushPermission,
      removeNotification,
      clearAll: clearAllNotifications,
      scheduleOnce(id, date, payload) {
        const timers = timersRef.current;
        if (timers.has(id)) {
          clearTimeout(timers.get(id));
          timers.delete(id);
        }
        const delay = new Date(date).getTime() - now().getTime();
        if (delay <= 0) { notify(payload); return; }
        const t = setTimeout(() => {
          notify(payload);
          timers.delete(id);
        }, delay);
        timers.set(id, t);
      },
      clear(id) {
        const timers = timersRef.current;
        if (timers.has(id)) {
          clearTimeout(timers.get(id));
          timers.delete(id);
        }
      },
      clearTimers() {
        const timers = timersRef.current;
        timers.forEach((t) => clearTimeout(t));
        timers.clear();
      },
    }),
    [prefs, notifyAndInbox, removeNotification, clearAllNotifications] // notify closes over current prefs; acceptable here
  );

  // cleanup
  useEffect(() => {
    return () => {
      const timers = timersRef.current;
      timers.forEach((t) => clearTimeout(t));
      timers.clear();
    };
  }, []);

  // listen for external events (e.g., modules outside React) requesting notifications
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const handler = (event) => {
      const detail = event.detail;
      if (detail && typeof detail === "object") {
        addNotification(detail);
      }
    };
    window.addEventListener("mudo:add-notification", handler);
    return () => window.removeEventListener("mudo:add-notification", handler);
  }, [addNotification]);

  const notifications = useMemo(() => {
    const merged = new Map();
    [...localNotifications, ...remoteNotifications].forEach((n) => {
      if (!n?.id) return;
      merged.set(n.id, normalizeNotification(n));
    });
    return Array.from(merged.values()).sort((a, b) => {
      const aTime = parseTimestamp(a.createdAt || a.created_at) || 0;
      const bTime = parseTimestamp(b.createdAt || b.created_at) || 0;
      return bTime - aTime;
    });
  }, [localNotifications, remoteNotifications]);

  const unreadCount = useMemo(
    () => (notifications || []).reduce((total, n) => (isUnread(n) ? total + 1 : total), 0),
    [notifications]
  );
  const getUnreadCount = useCallback(
    () => (notifications || []).reduce((total, n) => (isUnread(n) ? total + 1 : total), 0),
    [notifications]
  );
  const offerNotificationCount = offerNotifications.length;
  const totalNotificationCount = unreadCount + offerNotificationCount;

  return (
    <NotificationsContext.Provider
      value={{
        ...api,
        notifications,
        loading,
        refresh,
        addNotification,
        addSmartNotification,
        refreshSmartNotifications,
        markRead,
        markAsRead: markRead,
        markAllRead,
        markAllAsRead: markAllRead,
        unreadCount,
        getUnreadCount,
        offerNotifications,
        setOfferNotifications,
        offerNotificationCount,
        totalNotificationCount,
      }}
    >
      {children}
    </NotificationsContext.Provider>
  );
}

export function useNotificationsForWorld(world) {
  const ctx = useNotifications();
  const { unreadCount: unreadMessages } = useUnreadCount();
  
  return useMemo(() => {
    if (!ctx) {
      return {
        notifications: [],
        unreadCount: unreadMessages || 0,
        markRead: () => {},
        markAllRead: () => {},
        addNotification: () => {},
      };
    }
    const scoped = (ctx.notifications || []).filter((n) => matchesWorld(n.world, world));
    const unreadNotifications = scoped.reduce((total, n) => (isUnread(n) ? total + 1 : total), 0);
    // Combine unread notifications with unread messages count
    const unreadCount = unreadNotifications + (unreadMessages || 0);
    
    return {
      notifications: scoped,
      unreadCount,
      markRead: ctx.markRead,
      markAllRead: () => ctx.markAllRead(world),
      addNotification: ctx.addNotification,
      loading: ctx.loading,
      refresh: ctx.refresh,
    };
  }, [ctx, world, unreadMessages]);
}
